import React, { useCallback, useMemo, useState } from "react"
import styles from "./chooser.module.css"

export type ChooserProps = {
  title: string
  subtitle?: string
  open?: boolean
  defaultOpen?: boolean
  onToggle?: (open: boolean) => void
  className?: string
  children?: React.ReactNode
}

/**
 * Accordion-style panel (same behaviour as gift-wrap Chooser). Content is consumer-supplied.
 */
export function Chooser({
  title,
  subtitle = "",
  open,
  defaultOpen = false,
  onToggle,
  className = "",
  children,
}: ChooserProps) {
  const isControlled = typeof open === "boolean"
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen)
  const isOpen = isControlled ? open : internalOpen

  const handleToggle = useCallback(() => {
    const next = !isOpen
    if (!isControlled) {
      setInternalOpen(next)
    }
    onToggle?.(next)
  }, [isOpen, isControlled, onToggle])

  const containerClass = useMemo(
    () =>
      `${styles["chooser"]} chooser ${isOpen ? `${styles["chooser-open"]} chooser-open` : ""} ${className}`.trim(),
    [isOpen, className],
  )

  return (
    <div className={containerClass}>
      {isOpen ? <div className={styles["chooser--content"]}>{children}</div> : null}
      <div
        className={styles["chooser--trigger"]}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleToggle()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={styles["chooser--icon"]}>
          {isOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#333">
              <path d="m356-160-56-56 180-180 180 180-56 56-124-124-124 124Zm124-404L300-744l56-56 124 124 124-124 56 56-180 180Z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#333">
              <path d="M480-120 300-300l58-58 122 122 122-122 58 58-180 180ZM358-598l-58-58 180-180 180 180-58 58-122-122-122 122Z" />
            </svg>
          )}
        </div>
        <div className={styles["chooser--trigger-content"]}>
          <p className={styles["chooser--trigger-content-title"]}>{title}</p>
          {subtitle ? <p className={styles["chooser--trigger-content-selected-title"]}>{subtitle}</p> : null}
        </div>
      </div>
    </div>
  )
}
